import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LOGO_URL = "/assets/images/logo.png";

const Header = () => {
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { id: 'what-we-do', label: t.nav.whatWeDo },
    { id: 'how-it-works', label: t.nav.howItWorks },
    { id: 'experiences', label: t.nav.experiences },
    { id: 'services', label: t.nav.services },
    { id: 'why-us', label: t.nav.whyUs },
    { id: 'contact', label: t.nav.contact },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center group"
            data-testid="header-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Kaesthetic World" 
              className="h-16 md:h-20 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="relative text-xs tracking-[0.12em] uppercase text-zinc-500 hover:text-white transition-colors duration-300 py-2 group"
                data-testid={`nav-${link.id}`}
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-full h-px bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </button>
            ))}
          </nav>

          {/* Right side: Language Switcher + CTA */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger 
                className="flex items-center gap-2 px-3 py-2 text-xs tracking-[0.1em] uppercase text-zinc-500 hover:text-white transition-colors border border-white/10 hover:border-white/30"
                data-testid="language-switcher"
              >
                <span className="text-base">{currentLanguage.flag}</span>
                <span className="font-medium">{currentLanguage.code.toUpperCase()}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="bg-black border border-white/10 min-w-[180px] p-2"
                data-testid="language-dropdown"
              >
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-3 cursor-pointer px-3 py-2.5 transition-colors ${
                      lang.code === currentLanguage.code 
                        ? 'text-white bg-white/5' 
                        : 'text-zinc-500 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span className="text-sm">{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA - Minimal */}
            <button
              onClick={() => scrollToSection('contact')}
              className="flex items-center gap-2 text-xs tracking-[0.1em] uppercase text-zinc-400 hover:text-white transition-colors duration-300 group"
              data-testid="header-cta"
            >
              <span>Partnership</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden glass border-t border-white/5 animate-slide-down"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-6 py-8">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-left text-zinc-400 hover:text-white py-3 text-sm tracking-[0.1em] uppercase transition-colors border-b border-white/5"
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Mobile Language Switcher */}
            <div className="mt-8 pt-6 border-t border-white/5">
              <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-4">Language</p>
              <div className="grid grid-cols-4 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex flex-col items-center gap-1.5 p-3 transition-all ${
                      lang.code === currentLanguage.code 
                        ? 'bg-white/10 text-white' 
                        : 'hover:bg-white/5 text-zinc-500 hover:text-white'
                    }`}
                    data-testid={`mobile-lang-${lang.code}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-[10px] tracking-wider uppercase">{lang.code}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile CTA */}
            <button
              onClick={() => scrollToSection('contact')}
              className="btn-primary w-full mt-8 text-center"
              data-testid="mobile-cta"
            >
              {t.hero.cta1}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
